/**
 * WindowManager - Управление Popup Окнами
 * 
 * Модуль для управления popup окнами в приложении.
 * Позволяет открывать окна и автоматически переиспользовать уже открытые окна.
 */

class WindowManager {
    constructor() {
        // Store references to opened windows
        this.openedWindows = {};
    }
    
    /**
     * Open a popup window or navigate existing one
     * @param {string} url - URL to open
     * @param {string} windowName - Name identifier for the window
     * @param {string} windowFeatures - Window features string
     * @param {number} windowId - Optional ID for the window (defaults to 1)
     * @returns {Window|null} - Reference to the opened window or null if failed
     */
    openWindow(url, windowName, windowFeatures, windowId = 1) {
        const fullWindowName = `${windowName}_${windowId}`;
        
        // Check if window already exists and is not closed
        if (this.openedWindows[fullWindowName] && !this.openedWindows[fullWindowName].closed) {
            // Window exists, try to focus it and navigate to new URL
            try {
                this.openedWindows[fullWindowName].focus();
                this.openedWindows[fullWindowName].location.href = url;
                return this.openedWindows[fullWindowName];
            } catch (e) {
                // Window might be blocked or inaccessible, remove from tracking
                delete this.openedWindows[fullWindowName];
            }
        }
        
        // Open new window
        const newWindow = window.open(url, fullWindowName, windowFeatures);
        
        if (newWindow) {
            // Store reference to the new window
            this.openedWindows[fullWindowName] = newWindow;
            
            // Add event listener to clean up when window is closed
            newWindow.addEventListener('beforeunload', () => {
                delete this.openedWindows[fullWindowName];
            });
            
            return newWindow;
        }
        
        return null;
    }
    
    /**
     * Close a specific window
     * @param {string} windowName - Name identifier for the window
     * @param {number} windowId - Optional ID for the window (defaults to 1)
     */
    closeWindow(windowName, windowId = 1) {
        const fullWindowName = `${windowName}_${windowId}`;
        
        if (this.openedWindows[fullWindowName] && !this.openedWindows[fullWindowName].closed) {
            this.openedWindows[fullWindowName].close();
            delete this.openedWindows[fullWindowName];
        }
    }
    
    /**
     * Close all windows with a specific name
     * @param {string} windowName - Name identifier for the window
     */
    closeAllWindows(windowName) {
        Object.keys(this.openedWindows).forEach(key => {
            if (key.startsWith(windowName + '_')) {
                this.closeWindow(windowName, key.split('_')[1]);
            }
        });
    }
    
    /**
     * Check if a window is open
     * @param {string} windowName - Name identifier for the window
     * @param {number} windowId - Optional ID for the window (defaults to 1)
     * @returns {boolean} - True if window is open
     */
    isWindowOpen(windowName, windowId = 1) {
        const fullWindowName = `${windowName}_${windowId}`;
        return !!(this.openedWindows[fullWindowName] && !this.openedWindows[fullWindowName].closed);
    }
    
    /**
     * Get all opened windows
     * @returns {Object} - Object with all opened windows
     */
    getOpenedWindows() {
        return { ...this.openedWindows };
    }
    
    /**
     * Get count of opened windows with specific name
     * @param {string} windowName - Name identifier for the window
     * @returns {number} - Count of opened windows
     */
    getWindowCount(windowName) {
        return Object.keys(this.openedWindows).filter(key => 
            key.startsWith(windowName + '_')
        ).length;
    }
}

// Create singleton instance
const windowManager = new WindowManager();

// Export for different module systems
if (typeof module !== 'undefined' && module.exports) {
    // CommonJS
    module.exports = windowManager;
} else if (typeof define === 'function' && define.amd) {
    // AMD
    define([], function() {
        return windowManager;
    });
} else {
    // Browser global
    window.WindowManager = windowManager;
}

export default windowManager; 